/**
 * advicetab.ts — the Trade Management tab: what the advisor said, and whether he acted.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Stage-1 trade management (shipped 2026-08-09) reads the open positions every
 * morning, decides whether each stop can move toward its target, and sends ONE
 * Telegram message. It PROPOSES ONLY — it structurally cannot execute, because
 * signing is per-user on a Cognito sub and a cron job has no user.
 *
 * Andrei then asked where he could SEE those suggestions, and chose the fuller
 * answer: keep the record, and show it next to whether he acted on it. That record
 * is what answers "was the advice any good", which is the entire question stage 1
 * exists to settle before anything is armed.
 *
 * THE ONE RULE, INHERITED FROM tradepanel.ts: THIS FILE RENDERS.
 * It decides no stop, recomputes no R, and places nothing. The advised numbers come
 * from the route verbatim; the live numbers come from the account snapshot verbatim.
 *
 * NO-CHANGE ROWS ARE SHOWN. "We looked at BTC and there was nothing to do" is the
 * evidence the advisor was WATCHING. If only moves appeared, a silent failure and a
 * quiet market would look identical — which is exactly the defect that hid 14
 * unfetched listings every morning for weeks (f487f4f). They render QUIETLY, as
 * secondary rows, so they are evidence rather than noise.
 */
import {
  emptyState, esc, fmtPx, marketCell, type PositionRow, type Snapshot,
} from './tradepanel';
import { type TicketState } from './ticket';

/** One row of `GET /api/manage/advice` — one position, one run of the advisor.
 *
 *  ASSUMED SHAPE (stream 1 owns the route). It mirrors
 *  `chartapp/lambdas/manage/advice.py::Proposal.as_dict()` field for field, plus the
 *  `advised_at` the store stamps on the run and the same `available` envelope
 *  `/api/setups` uses. If the route lands on different names this type is the ONE
 *  place that changes.
 *
 *  `asset` is carried BYTE FOR BYTE. `xyz:NATGAS` is not `XYZ:NATGAS`: it is a
 *  different partition of the table upstream and a different symbol to the venue,
 *  so nothing on this path upper-cases or normalises it.
 *
 *  Every price is nullable, and a null one stays an ABSENCE — see `absent`. */
export type AdviceRow = {
  asset: string;
  side: string;
  verdict: 'move_stop' | 'no_change' | string;
  /** Plain English, written by the advisor. Printed verbatim, never re-worded. */
  reason: string;
  current_stop: number | null;
  proposed_stop: number | null;
  r_locked: number | null;
  r_gain?: number | null;
  entry?: number | null;
  ref_price?: number | null;
  /** Epoch SECONDS, as `/api/setups` sends `sent_at`. */
  advised_at: number | null;
};

export type AdvicePayload = {
  available: boolean;
  reason?: string | null;
  advice: AdviceRow[];
};

/** A missing level or figure, in WORDS. Never `0`, never a dash in a price column.
 *
 *  This repo has already shipped a cell that fabricated `+220.5` next to its own
 *  "no target advised" (fixed in the setups tab). `0` is not a missing stop, it is a
 *  WRONG stop; a dash sits in a price column and reads like every number beside it;
 *  a blank cell reads as "nothing to worry about". So the cell says what is true. */
const absent = (what: string): string => `<span class="muted">${esc(what)}</span>`;

/** How long ago the advisor said this, in words. */
export const advisedAgo = (at: number | null | undefined, now = Date.now()): string => {
  if (at === null || at === undefined || !Number.isFinite(at)) return 'time not recorded';
  const mins = Math.max(0, Math.round((now - at * 1000) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
};

/** How close two prices have to be to count as THE SAME stop.
 *
 *  Relative, not absolute: this book spans XYZ100 at ~28,771 and VELO at ~0.0036, so
 *  one fixed epsilon is either meaningless at the top or wrong at the bottom. 0.1% is
 *  wide enough to absorb the exchange rounding a trigger to its own tick size, and far
 *  narrower than any stop move the advisor proposes (the threshold is 0.10R).
 *
 *  ponytail: a relative tolerance rather than the venue's real tick size. Tick sizes
 *  live on the exchange, not in a renderer; ask the snapshot for them if this ever
 *  mis-reads a stop he did move. */
const SAME_PRICE_REL = 0.001;
const samePrice = (a: number, b: number): boolean =>
  Math.abs(a - b) <= Math.abs(b) * SAME_PRICE_REL;

/** Every live stop level protecting a position, from the ORDER GRAPH.
 *
 *  `protection.legs` is derived server-side by the audited `hl_check.leg_kind`, so
 *  this asks the one component that already polices which leg is a stop rather than
 *  forming a second opinion. `protection.sl` is the same graph's summary and is
 *  folded in so a position whose legs list is empty but whose summary is not still
 *  compares. */
const liveStops = (pos: PositionRow): number[] => {
  const out: number[] = [];
  for (const leg of pos.protection.legs) {
    if (leg.kind !== 'SL') continue;
    const px = leg.trigger_px ?? leg.limit_px;
    if (px !== null && px !== undefined && Number.isFinite(px) && px > 0) out.push(px);
  }
  const summary = pos.protection.sl;
  if (summary !== null && Number.isFinite(summary) && summary > 0
      && !out.some((p) => samePrice(p, summary))) {
    out.push(summary);
  }
  return out;
};

/** THE STOP THAT BINDS THE RISK, when the advised one is not on the book.
 *
 *  NOT `stops[0]` (R4-10, high, fixed 2026-08-21). That was the first element of the
 *  list `liveStops` happened to assemble — leg order as `/info` returned it, with the
 *  summary appended last — so the sentence "not yet, stop is at 61,000" could name any
 *  rung of a ladder while 65,602 was the price actually standing between the position
 *  and a loss. Andrei reads that sentence as a fact about his risk.
 *
 *  IT IS NOT THE SAME FIX AS R4-2 / t_66e1c72f, and deliberately not routed through it.
 *  That card matches a leg by ORDER IDENTITY because it is reading back a write WE made,
 *  and the id of that write is the only thing that identifies it. Here no write happened
 *  and there is no id to match: the question is which of several resting legs decides the
 *  loss, and that has a different answer — the TIGHTEST one, because it fires first.
 *
 *  IT ASKS THE SERVER FIRST. `protection.sl` is `hl_account._protective_legs`' own
 *  answer to exactly this question (`max(sl_px)` for a long, `min` for a short, over
 *  legs classified by the audited `hl_check.leg_kind`), and it is what the panel's TP/SL
 *  column and the chart's SL line already draw. Re-deriving it here would be a second
 *  opinion about one number — the defect class this file's own header warns about — so
 *  the leg scan below is a FALLBACK for the one shape the summary cannot cover (legs
 *  present, summary null), applying the same rule rather than a different one.
 *
 *  `null` when nothing usable rests: the caller must not print a price it does not have. */
export function bindingStop(pos: PositionRow): number | null {
  const summary = pos.protection.sl;
  if (summary !== null && Number.isFinite(summary) && summary > 0) return summary;
  const legs: number[] = [];
  for (const leg of pos.protection.legs) {
    if (leg.kind !== 'SL') continue;
    const px = leg.trigger_px ?? leg.limit_px;
    if (px !== null && px !== undefined && Number.isFinite(px) && px > 0) legs.push(px);
  }
  if (!legs.length) return null;
  // The same max/min rule as `_protective_legs`: the stop that fires first IS the risk.
  return pos.side === 'long' ? Math.max(...legs) : Math.min(...legs);
}

/** The "did I act?" verdict. `kind: 'unknown'` is a FIRST-CLASS answer, not a failure.
 *
 *  `text` is the whole cell — it is always a sentence a trader can act on, and it is
 *  never a tick that cannot be justified. */
export type ActVerdict = { kind: 'yes' | 'no' | 'unknown'; text: string };

/**
 * Did he act on this row? Derived CLIENT-SIDE from the account snapshot the panel
 * already holds.
 *
 * WHY IT IS DERIVED AND NOT STORED. His stops live on the exchange. The snapshot is
 * the panel's reading of them, audited by the review engine. Asking the advice store
 * to remember "he acted" would create a SECOND source of truth about where his stops
 * are, and the second one is always the stale one.
 *
 * IT IS HONEST WHEN IT CANNOT TELL, and three genuinely common situations cannot be
 * told:
 *   * the position is gone — a closed trade's stop cannot be compared to anything,
 *     and "no" would read as a reproach for a trade he correctly exited;
 *   * there is no stop order at all — that is a finding in its own right (the review
 *     engine raises it), and it is NOT the same fact as "he ignored the advice";
 *   * the advisor proposed no stop (every `no_change` row) — there was nothing to do,
 *     so there is nothing he could have done or failed to do.
 *
 * In each case the cell says so IN WORDS. It never shows a tick it cannot justify.
 */
export function didIAct(row: AdviceRow, snap: Snapshot | null): ActVerdict {
  if (row.proposed_stop === null || !Number.isFinite(row.proposed_stop)) {
    // Every `no_change` row lands here, and that is the point: nothing was advised,
    // so "yes" and "no" are both claims about an action that was never available.
    return { kind: 'unknown', text: 'nothing was advised' };
  }
  if (!snap) return { kind: 'unknown', text: 'account not loaded yet' };
  const pos = snap.positions.find((p) => p.coin === row.asset);
  if (!pos) return { kind: 'unknown', text: 'position closed since' };
  const stops = liveStops(pos);
  if (!stops.length) return { kind: 'unknown', text: 'no stop order to compare' };
  if (stops.some((p) => samePrice(p, row.proposed_stop as number))) {
    return { kind: 'yes', text: 'yes, stop is there' };
  }
  // THE BINDING STOP, not `stops[0]` (R4-10). See `bindingStop`: the sentence is read
  // as a statement about risk, so it must name the price that decides it. `null` cannot
  // happen here (`stops.length` is non-zero above, and `bindingStop` returns a price
  // from the same legs), but it is answered as `unknown` rather than printed as a
  // missing number — a formatted `null` in this cell would be a claim of its own.
  const binding = bindingStop(pos);
  if (binding === null) return { kind: 'unknown', text: 'no stop order to compare' };
  return { kind: 'no', text: `not yet, stop is at ${fmtPx(binding)}` };
}

/** The ticket state after an ADVICE ROW is clicked. PURE, so a gate can pin it.
 *
 *  It loads the ADVISED STOP onto a ticket that MANAGES the existing position —
 *  `managing` set, size and leverage taken from the live position — because that is
 *  what the advice is about. A new-entry ticket carrying an entry and a stop would,
 *  if he pressed Place, OPEN A SECOND POSITION in the market he was being advised to
 *  protect. That is the one misread this path must not make possible.
 *
 *  NULL when there is no live position for the row: the honest ticket for a closed
 *  trade is no ticket. The caller charts the market and says so.
 *
 *  IT PLACES NOTHING. It fills a form. Arming still goes through the ticket's own
 *  Place button, which derives its own size and runs the two-phase preview.
 *
 *  `symbol` is assigned byte for byte, for the reason on `AdviceRow.asset`. */
export function ticketFromAdvice(
  st: TicketState, snap: Snapshot | null, row: AdviceRow,
): TicketState | null {
  const pos = snap?.positions.find((p) => p.coin === row.asset);
  if (!pos) return null;
  const advised = row.proposed_stop !== null && Number.isFinite(row.proposed_stop)
    && (row.proposed_stop as number) > 0
    ? row.proposed_stop
    : null;
  return {
    ...st,
    symbol: pos.coin,
    side: pos.side === 'long' ? 'buy' : 'sell',
    entry: pos.entry_px,
    // The ADVISED stop when there is one; otherwise the stop he already has, never a
    // guess and never zero. A `no_change` row has nothing to propose, and the honest
    // ticket for it holds the position exactly as it stands.
    stop: advised ?? pos.protection.sl,
    tps: [pos.protection.tp ?? null, null],
    leverage: pos.leverage ?? st.leverage,
    sizing: 'size',
    size: pos.size,
    tr_id: null,
    managing: pos.coin,
    // NOTHING HAS BEEN MOVED YET. Both legs above were IMPORTED off the live position, and
    // that is exactly why the manage verb may not be inferred from them (R4-3, critical):
    // an advised stop sitting in `stop` is a suggestion the user has not acted on. The
    // press is refused until a line is dragged or a field typed.
    movedLeg: null,
    // WHAT THE EXCHANGE HOLDS RIGHT NOW, so a press that would re-send an unchanged price
    // is refused rather than paying a cancel+replace for a no-op. The ADVISED stop is not
    // recorded here — `stop` above may already be the advice, and comparing the advice
    // against itself would refuse the very move this row exists to make.
    managedFrom: { stop: pos.protection.sl ?? null, tp: pos.protection.tp ?? null },
  };
}

/** What the advisor decided, in his words, plus the R it locks. */
const verdictCell = (row: AdviceRow): string => {
  const moves = row.verdict === 'move_stop';
  const label = moves ? 'Move the stop' : 'Leave it alone';
  const locked = moves && row.r_locked !== null && Number.isFinite(row.r_locked)
    ? ` <span class="muted">locks ${(row.r_locked as number).toFixed(2)}R</span>`
    : '';
  return `<b>${label}</b>${locked}`;
};

/**
 * The advice history: one row per position per run, newest first as the route sends
 * them.
 *
 * Clicking a row charts that market on all three panes and loads the advised stop
 * into the ticket. Handled in main.ts, which owns the chart and the ticket; this
 * module only renders, which is what stops two places fighting over one level list.
 */
export function renderAdvice(
  payload: AdvicePayload | null, snap: Snapshot | null, err: string | null = null,
): string {
  const head = `<div class="tp-note">Every morning after the close, each open trade is
    checked for whether its stop can move toward its target. This is the record —
    including the mornings when the answer was to leave a trade alone. Nothing here
    has been placed for you: clicking a row loads the advised stop into the order
    ticket, and the Place button stays yours.</div>`;
  if (err) return `${head}${emptyState('The advice history could not be read', err)}`;
  if (!payload) return `${head}${emptyState('No advice yet', 'Reading the advice history…')}`;
  if (!payload.available) {
    // NOT an empty table. An empty table reads as "the advisor looked and found
    // nothing today", which is the opposite of "the advisor is not connected here".
    return `${head}${emptyState('The morning stop review is not connected here',
      payload.reason
      ?? 'Advice appears here once this app is wired to the daily stop review.')}`;
  }
  if (!payload.advice.length) {
    return `${head}${emptyState('No advice recorded yet',
      'The stop review has not run since this record started, or you had no open trades when it did.')}`;
  }
  const rows = payload.advice
    .map((a, i) => {
      const dex = a.asset && a.asset.includes(':') ? a.asset.split(':')[0] : 'core';
      const moves = a.verdict === 'move_stop';
      const act = didIAct(a, snap);
      // The row's INDEX into the payload, not its prices. A click then reads the row
      // object the render used, so the ticket cannot be filled from a stringified,
      // re-parsed copy of a level — `Number('')` is 0, and a stop of zero is a WRONG
      // stop rather than a missing one.
      return `
      <tr data-advice="${i}" data-coin="${esc(a.asset)}"${moves ? '' : ' class="quiet"'}>
        <td>${marketCell(a.asset, dex, `<span class="muted tiny"> ${esc(a.side)}</span>`)}</td>
        <td>${verdictCell(a)}
            <div class="muted tiny">${esc(a.reason)}</div></td>
        <td class="n">${a.current_stop === null
          ? absent('no stop then') : fmtPx(a.current_stop)}</td>
        <td class="n">${a.proposed_stop === null
          ? absent('no stop advised') : fmtPx(a.proposed_stop)}</td>
        <td>${esc(advisedAgo(a.advised_at))}</td>
        <td class="act ${act.kind}">${esc(act.text)}</td>
      </tr>`;
    })
    .join('');
  return `${head}<table class="tp-table">
    <thead><tr><th>Market</th><th>What to do</th><th class="n">Stop then</th>
      <th class="n">Advised stop</th><th>When</th><th>Did you do it?</th></tr></thead>
    <tbody>${rows}</tbody></table>`;
}
